<script setup lang="ts">
import { computed } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import ClickStat from "@Modules/analytics/DTOs/ClickStat";
import { ClickChartDataProvider } from "@Modules/analytics/DTOs/TimelineItemCollection";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

//
// Setup
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
interface Props {
  availableStats: (keyof ClickStat)[],
  dataset: ClickChartDataProvider,
  loading?: boolean,
  simple?: boolean,
};
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  simple: false,
});

//
// Computed
const computedTotal = computed<ClickStat>(() => props.dataset?.getChartTotal());

//
// Methods
function getStat(field: keyof ClickStat): string {
  return computedTotal.value?.[field].toLocaleString() ?? "";
}

function translate(field: keyof ClickStat): string {
  /** @TODO: Add these as actual translations */
  const translations: Record<keyof Omit<ClickStat, "getChartData" | "getChartTotal">, { simple: string, full:string }> = {
    name: {simple: "Name", full: "Name" },
    bots: {simple: "Total Bots Clicks", full: "Clicked Links" },
    human: {simple: "Likely Human Clicks", full: "Likely Human Clicks" },
    unique: {simple: "Unique Clicks", full: "Unique Clicks" },
    total: {simple: "Total Clicks", full: "Clicked Links" },
    visitors: {simple: "Total Clicks", full: "Unique Visitors" },
    active: { simple: "Clicked Links", full: "Clicked Links" },
  };

  return props.simple
    ? translations[field as keyof typeof translations].simple
    : translations[field as keyof typeof translations].full;
}
</script>

<template>
  <div
    class="_quickStatList stats-quick"
    :class="{ 'stats-quick--mobile': isMobileView }"
  >
    <ChartLayout
      v-for="field in props.availableStats"
      :key="field"
      class="stats-quick__item"
      :class="[`_quickStat _${field}`]"
      :is-loading="loading"
      is-compact
    >
      <template #header>
        <p class="stats-quick__title">
          {{ translate(field) }}
        </p>
      </template>
      <h3 :class="`_${field}_val stats-quick__stat`">
        {{ getStat(field) }}
      </h3>
    </ChartLayout>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.stats-quick {
  $self: &;

  display: flex;
  gap: spacing.$xxs;

  &__item {
    display: flex;
    flex-flow: column;
    justify-content: center;
    align-items: center;
    flex: 1;

    padding: spacing.$xxxs spacing.$xxs;
  }

  &__title {
    font-size: typography.$s-font-size;
    line-height: typography.$s-line-height;
    font-weight: typography.$font-weight-semi-bold;
  }

  &__stat {
    text-align: center;
    font-size: typography.$h3-font-size;
    line-height: typography.$h3-line-height;
    font-weight: typography.$font-weight-bold;
  }

  &__title,
  &__stat {
    margin: 0;
  }

  &--mobile {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
